import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, Edit, Search, Users, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const FranchiseeManagement = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isHQAdmin = user?.role === 'HQ_ADMIN';
  const isSystemAdmin = user?.role === 'SYSTEM_ADMIN';
  const canEditMainKeyPerson = isHQAdmin || isSystemAdmin;
  const isFranchiseeOperator = user?.role === 'FRANCHISEE_OPERATOR';
  const canSearchUsers = ['HQ_ADMIN', 'SYSTEM_ADMIN', 'REGIONAL_COORDINATOR'].includes(user?.role);
  
  const [franchisees, setFranchisees] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [keyPersonDialog, setKeyPersonDialog] = useState(false);
  const [searchDialog, setSearchDialog] = useState(false);
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [keyPersonData, setKeyPersonData] = useState({
    main_key_person_id: '',
    secondary_key_person_id: ''
  });

  const [formData, setFormData] = useState({
    company_name: '',
    operator_name: '',
    franchise_id: '',
    email: '',
    phone: '',
    kindergarten: {
      name: '',
      location: '',
      capacity: 0,
      current_enrollment: 0,
      staff_count: 0,
      contact_person: '',
      contact_phone: ''
    },
    agreement_start_date: '',
    agreement_expiry_date: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchFranchisees();
    fetchAllUsers();
  }, [token]);

  const fetchFranchisees = async () => {
    try {
      const response = await axios.get(`${API}/franchisees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchisees(response.data);
    } catch (error) {
      toast.error('Failed to fetch franchisees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/users/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  };

  const openKeyPersonDialog = (franchisee) => {
    setSelectedFranchisee(franchisee);
    setKeyPersonData({
      main_key_person_id: franchisee.license?.main_key_person_id || '',
      secondary_key_person_id: franchisee.license?.secondary_key_person_id || ''
    });
    setKeyPersonDialog(true);
  };

  const handleUpdateKeyPersons = async () => {
    try {
      const payload = {};
      if (canEditMainKeyPerson && keyPersonData.main_key_person_id) {
        payload.main_key_person_id = keyPersonData.main_key_person_id;
      }
      if (keyPersonData.secondary_key_person_id) {
        payload.secondary_key_person_id = keyPersonData.secondary_key_person_id;
      }
      
      await axios.patch(
        `${API}/franchisees/${selectedFranchisee.franchisee_id}/key-persons`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Key persons updated successfully');
      setKeyPersonDialog(false);
      fetchFranchisees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update key persons');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/franchisees`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Franchisee created successfully');
      setOpenDialog(false);
      fetchFranchisees();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create franchisee');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      operator_name: '',
      franchise_id: '',
      email: '',
      phone: '',
      kindergarten: {
        name: '',
        location: '',
        capacity: 0,
        current_enrollment: 0,
        staff_count: 0,
        contact_person: '',
        contact_phone: ''
      },
      agreement_start_date: '',
      agreement_expiry_date: '',
      status: 'Active'
    });
  };

  const handleView = (franchisee) => {
    setSelectedFranchisee(franchisee);
    setViewDialog(true);
  };

  const handleEdit = (franchisee) => {
    setFormData({
      company_name: franchisee.company_name,
      operator_name: franchisee.operator_name,
      franchise_id: franchisee.franchise_id,
      email: franchisee.email,
      phone: franchisee.phone,
      kindergarten: franchisee.kindergarten,
      license: franchisee.license || {},
      agreement_start_date: franchisee.agreement_start_date,
      agreement_expiry_date: franchisee.agreement_expiry_date,
      status: franchisee.status
    });
    setSelectedFranchisee(franchisee);
    setEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/franchisees/${selectedFranchisee.franchisee_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Franchisee updated successfully');
      setEditDialog(false);
      fetchFranchisees();
    } catch (error) {
      toast.error('Failed to update franchisee');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="franchisee-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Franchisee Management</h1>
            <p className="text-muted-foreground mt-1">Manage all franchise operations</p>
          </div>
          <div className="flex gap-2">
            {canSearchUsers && (
              <Button variant="outline" onClick={() => setSearchDialog(true)}>
                <Search className="h-4 w-4 mr-2" />
                Search Person
              </Button>
            )}
            {isHQAdmin && (
              <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-franchisee-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Franchisee
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Franchisee</DialogTitle>
                  <DialogDescription>Enter franchisee and kindergarten details</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name*</Label>
                      <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} required data-testid="company-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Operator Name*</Label>
                      <Input value={formData.operator_name} onChange={(e) => setFormData({...formData, operator_name: e.target.value})} required data-testid="operator-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Franchise ID*</Label>
                      <Input value={formData.franchise_id} onChange={(e) => setFormData({...formData, franchise_id: e.target.value})} required data-testid="franchise-id-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email*</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone*</Label>
                      <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required data-testid="phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Agreement Start Date*</Label>
                      <Input type="date" value={formData.agreement_start_date} onChange={(e) => setFormData({...formData, agreement_start_date: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Agreement Expiry Date*</Label>
                      <Input type="date" value={formData.agreement_expiry_date} onChange={(e) => setFormData({...formData, agreement_expiry_date: e.target.value})} required />
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3">Kindergarten Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Kindergarten Name*</Label>
                        <Input value={formData.kindergarten.name} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, name: e.target.value}})} required data-testid="kindergarten-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Location*</Label>
                        <Input value={formData.kindergarten.location} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, location: e.target.value}})} required data-testid="location-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Capacity*</Label>
                        <Input type="number" value={formData.kindergarten.capacity} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, capacity: parseInt(e.target.value)}})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Current Enrollment</Label>
                        <Input type="number" value={formData.kindergarten.current_enrollment} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, current_enrollment: parseInt(e.target.value)}})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Staff Count</Label>
                        <Input type="number" value={formData.kindergarten.staff_count} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, staff_count: parseInt(e.target.value)}})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Person*</Label>
                        <Input value={formData.kindergarten.contact_person} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, contact_person: e.target.value}})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Phone*</Label>
                        <Input value={formData.kindergarten.contact_phone} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, contact_phone: e.target.value}})} required />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-franchisee-btn">Create Franchisee</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Franchisees</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-muted-foreground">Loading...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Franchise ID</TableHead>
                    <TableHead>Kindergarten</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {franchisees.map((franchisee) => (
                    <TableRow key={franchisee.franchisee_id} data-testid={`franchisee-row-${franchisee.franchise_id}`}>
                      <TableCell className="font-medium">{franchisee.company_name}</TableCell>
                      <TableCell>{franchisee.franchise_id}</TableCell>
                      <TableCell>{franchisee.kindergarten.name}</TableCell>
                      <TableCell>{franchisee.kindergarten.location}</TableCell>
                      <TableCell>
                        <Badge variant={franchisee.status === 'Active' ? 'default' : 'secondary'}>
                          {franchisee.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(franchisee)} data-testid={`view-franchisee-${franchisee.franchise_id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {(canEditMainKeyPerson || isFranchiseeOperator) && (
                            <Button variant="ghost" size="sm" onClick={() => openKeyPersonDialog(franchisee)} title="Edit Key Persons">
                              <UserCog className="h-4 w-4" />
                            </Button>
                          )}
                          {isHQAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(franchisee)} data-testid={`edit-franchisee-${franchisee.franchise_id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* View Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Franchisee Details</DialogTitle>
            </DialogHeader>
            {selectedFranchisee && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Company Name</p>
                    <p className="font-medium">{selectedFranchisee.company_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Operator</p>
                    <p className="font-medium">{selectedFranchisee.operator_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Franchise ID</p>
                    <p className="font-medium">{selectedFranchisee.franchise_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge>{selectedFranchisee.status}</Badge>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Kindergarten Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{selectedFranchisee.kindergarten.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{selectedFranchisee.kindergarten.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Capacity</p>
                      <p className="font-medium tabular-nums">{selectedFranchisee.kindergarten.capacity}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Enrollment</p>
                      <p className="font-medium tabular-nums">{selectedFranchisee.kindergarten.current_enrollment}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Franchisee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name*</Label>
                  <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Operator Name*</Label>
                  <Input value={formData.operator_name} onChange={(e) => setFormData({...formData, operator_name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Email*</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Phone*</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Status*</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="font-semibold mb-3">Kindergarten Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kindergarten Name*</Label>
                    <Input value={formData.kindergarten.name} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, name: e.target.value}})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Location*</Label>
                    <Input value={formData.kindergarten.location} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, location: e.target.value}})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity*</Label>
                    <Input type="number" value={formData.kindergarten.capacity} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, capacity: parseInt(e.target.value)}})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Enrollment</Label>
                    <Input type="number" value={formData.kindergarten.current_enrollment} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, current_enrollment: parseInt(e.target.value)}})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Staff Count</Label>
                    <Input type="number" value={formData.kindergarten.staff_count} onChange={(e) => setFormData({...formData, kindergarten: {...formData.kindergarten, staff_count: parseInt(e.target.value)}})} />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
                <Button type="submit">Update Franchisee</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Key Persons Dialog */}
        <Dialog open={keyPersonDialog} onOpenChange={setKeyPersonDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Key Persons</DialogTitle>
              <DialogDescription>
                {selectedFranchisee?.company_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {canEditMainKeyPerson && (
                <div className="space-y-2">
                  <Label>Main Key Person (Franchisee Operator)</Label>
                  <Select 
                    value={keyPersonData.main_key_person_id} 
                    onValueChange={(value) => setKeyPersonData({...keyPersonData, main_key_person_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Main Key Person" />
                    </SelectTrigger>
                    <SelectContent>
                      {allUsers.filter(u => u.role === 'FRANCHISEE_OPERATOR' || !u.franchisee_id).map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.full_name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Current: {selectedFranchisee?.license?.main_key_person_name || 'Not assigned'}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Secondary Key Person (Franchisee Manager)</Label>
                <Select 
                  value={keyPersonData.secondary_key_person_id} 
                  onValueChange={(value) => setKeyPersonData({...keyPersonData, secondary_key_person_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Secondary Key Person" />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers.filter(u => 
                      u.role === 'FRANCHISEE_MANAGER' || 
                      u.role === 'FRANCHISEE_SUPERVISOR' ||
                      (!u.franchisee_id && u.role !== 'HQ_ADMIN' && u.role !== 'SYSTEM_ADMIN')
                    ).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.full_name} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Current: {selectedFranchisee?.license?.secondary_key_person_name || 'Not assigned'}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setKeyPersonDialog(false)}>Cancel</Button>
                <Button onClick={handleUpdateKeyPersons}>Update Key Persons</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Search Person Dialog */}
        <Dialog open={searchDialog} onOpenChange={setSearchDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Search Person</DialogTitle>
              <DialogDescription>Search for a user to see their branch affiliations</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input 
                  placeholder="Search by name or email..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                />
                <Button onClick={searchUsers} disabled={searchLoading}>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-3">
                  {searchResults.map(result => (
                    <Card key={result.user_id}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{result.full_name}</p>
                            <p className="text-sm text-muted-foreground">{result.email}</p>
                          </div>
                          <Badge className={
                            result.role === 'HQ_ADMIN' ? 'bg-red-600 text-white' :
                            result.role === 'SYSTEM_ADMIN' ? 'bg-purple-600 text-white' :
                            result.role === 'REGIONAL_COORDINATOR' ? 'bg-blue-600 text-white' :
                            result.role === 'FRANCHISEE_OPERATOR' ? 'bg-emerald-600 text-white' :
                            result.role === 'FRANCHISEE_MANAGER' ? 'bg-cyan-600 text-white' :
                            'bg-slate-500 text-white'
                          }>
                            {result.role.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        
                        {result.affiliations.length > 0 ? (
                          <div className="mt-3">
                            <p className="text-sm font-medium mb-2">Branch Affiliations:</p>
                            <div className="space-y-1">
                              {result.affiliations.map((aff, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm bg-muted p-2 rounded">
                                  <span>{aff.company_name}</span>
                                  <Badge variant="outline">{aff.role_in_branch}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-3">No branch affiliations</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {searchResults.length === 0 && searchQuery && !searchLoading && (
                <p className="text-center text-muted-foreground py-4">No results found</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FranchiseeManagement;